import { useCallback, useState, useRef, type ChangeEvent } from 'react'
import { Upload, FolderOpen, ClipboardPaste } from 'lucide-react'
import { useContentCpStore } from '@/stores/useContentCpStore'
import { cn } from '@/lib/utils'

const SUPPORTED_EXTENSIONS = ['.pdf', '.txt', '.md', '.markdown', '.png', '.jpg', '.jpeg', '.webp', '.gif']

function isValidFile(file: File): boolean {
  // Check extension
  const ext = '.' + file.name.split('.').pop()?.toLowerCase()
  return SUPPORTED_EXTENSIONS.includes(ext)
}

// Recursively get all files from a directory entry
async function getAllFilesFromEntry(entry: FileSystemEntry): Promise<File[]> {
  const files: File[] = []

  if (entry.isFile) {
    const fileEntry = entry as FileSystemFileEntry
    const file = await new Promise<File>((resolve, reject) => {
      fileEntry.file(resolve, reject)
    })
    if (isValidFile(file)) {
      files.push(file)
    }
  } else if (entry.isDirectory) {
    const dirEntry = entry as FileSystemDirectoryEntry
    const reader = dirEntry.createReader()

    const readEntries = (): Promise<FileSystemEntry[]> =>
      new Promise((resolve, reject) => {
        reader.readEntries(resolve, reject)
      })

    // Read all entries (readEntries may need multiple calls for large directories)
    let entries: FileSystemEntry[] = []
    let batch: FileSystemEntry[]
    do {
      batch = await readEntries()
      entries = entries.concat(batch)
    } while (batch.length > 0)

    // Recursively process each entry
    for (const childEntry of entries) {
      const childFiles = await getAllFilesFromEntry(childEntry)
      files.push(...childFiles)
    }
  }

  return files
}

interface DropZoneProps {
  disabled?: boolean
  compact?: boolean
}

export function DropZone({ disabled = false, compact = false }: DropZoneProps) {
  const { addFileSource, addTextSource, addImageSource, batchState, sourceQueue } = useContentCpStore()
  // Auto-compact when we have sources in queue
  const isCompact = compact || sourceQueue.length > 0
  const [isDragging, setIsDragging] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const folderInputRef = useRef<HTMLInputElement>(null)
  const dragCounterRef = useRef(0)

  const isProcessing = batchState.isProcessing

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounterRef.current++
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true)
    }
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounterRef.current--
    if (dragCounterRef.current === 0) {
      setIsDragging(false)
    }
  }, [])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
      setIsDragging(false)
      dragCounterRef.current = 0

      if (disabled || isProcessing) return

      // Check if we have directory entries (for folder drops)
      const items = e.dataTransfer.items
      const allFiles: File[] = []

      if (items && items.length > 0) {
        // Use webkitGetAsEntry for potential directory support
        const entries: FileSystemEntry[] = []
        for (let i = 0; i < items.length; i++) {
          const entry = items[i].webkitGetAsEntry?.()
          if (entry) {
            entries.push(entry)
          }
        }

        if (entries.length > 0) {
          // Process all entries (files and directories)
          for (const entry of entries) {
            const files = await getAllFilesFromEntry(entry)
            allFiles.push(...files)
          }
        } else {
          // Fallback to regular file handling
          const files = Array.from(e.dataTransfer.files)
          allFiles.push(...files.filter(isValidFile))
        }
      } else {
        // Fallback to regular file handling
        const files = Array.from(e.dataTransfer.files)
        allFiles.push(...files.filter(isValidFile))
      }

      // Add all valid files
      allFiles.forEach((file) => {
        addFileSource(file)
      })

      // Show info about processed files
      if (allFiles.length > 0) {
        console.log(`Added ${allFiles.length} file(s) to queue`)
      }
    },
    [addFileSource, disabled, isProcessing]
  )

  const handleFileSelect = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files || [])
      const validFiles = files.filter(isValidFile)

      validFiles.forEach((file) => {
        addFileSource(file)
      })

      // Reset input
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    },
    [addFileSource]
  )

  const handleFolderSelect = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files || [])
      const validFiles = files.filter(isValidFile)

      validFiles.forEach((file) => {
        addFileSource(file)
      })

      // Reset input
      if (folderInputRef.current) {
        folderInputRef.current.value = ''
      }

      if (validFiles.length > 0) {
        console.log(`Added ${validFiles.length} file(s) from folder`)
      }
    },
    [addFileSource]
  )

  const handlePaste = useCallback(() => {
    // Read from clipboard
    navigator.clipboard
      .read()
      .then(async (items) => {
        for (const item of items) {
          // Check for images
          const imageType = item.types.find((t) => t.startsWith('image/'))
          if (imageType) {
            const blob = await item.getType(imageType)
            const reader = new FileReader()
            reader.onload = () => {
              const dataUrl = reader.result as string
              const base64Data = dataUrl.split(',')[1]
              addImageSource(
                [
                  {
                    data: base64Data,
                    mimeType: imageType,
                    name: `pasted-image-${Date.now()}.${imageType.split('/')[1]}`,
                  },
                ],
                undefined
              )
            }
            reader.readAsDataURL(blob)
            return
          }

          // Check for text
          if (item.types.includes('text/plain')) {
            const blob = await item.getType('text/plain')
            const text = await blob.text()
            if (text.trim().length > 20) {
              addTextSource(text.trim())
            }
          }
        }
      })
      .catch((err) => {
        console.error('Failed to read clipboard:', err)
      })
  }, [addImageSource, addTextSource])

  return (
    <div
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      className={cn(
        'relative border-2 border-dashed rounded-lg transition-colors w-full',
        isCompact ? 'p-3 sm:p-4' : 'p-6 sm:p-8',
        isDragging && !disabled && !isProcessing
          ? 'border-primary bg-primary/5'
          : 'border-border hover:border-muted-foreground/50',
        (disabled || isProcessing) && 'opacity-50 cursor-not-allowed'
      )}
    >
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={SUPPORTED_EXTENSIONS.join(',')}
        onChange={handleFileSelect}
        className="hidden"
        disabled={disabled || isProcessing}
      />
      {/* Folder input with webkitdirectory attribute */}
      <input
        ref={folderInputRef}
        type="file"
        // @ts-expect-error webkitdirectory is non-standard but widely supported
        webkitdirectory=""
        directory=""
        multiple
        onChange={handleFolderSelect}
        className="hidden"
        disabled={disabled || isProcessing}
      />

      <div className={cn(
        'flex items-center gap-3 text-center',
        isCompact ? 'flex-row justify-center' : 'flex-col'
      )}>
        {/* Icon - smaller in compact mode */}
        <div
          className={cn(
            'rounded-full flex-shrink-0',
            isCompact ? 'p-2' : 'p-3',
            isDragging ? 'bg-primary/10' : 'bg-muted/50'
          )}
        >
          <Upload
            className={cn(
              isDragging ? 'text-primary' : 'text-muted-foreground',
              isCompact ? 'w-4 h-4' : 'w-6 h-6'
            )}
            strokeWidth={1.5}
          />
        </div>

        {/* Text - hidden in compact mode on mobile */}
        <div className={cn('space-y-1', isCompact && 'hidden sm:block')}>
          <p className={cn('font-medium text-foreground', isCompact ? 'text-xs' : 'text-sm')}>
            {isDragging ? 'Drop files here' : isCompact ? 'Drop or add more' : 'Drop files here to add sources'}
          </p>
          {!isCompact && (
            <p className="text-xs text-muted-foreground">
              PDF, images, or text files
            </p>
          )}
        </div>

        {/* Buttons - inline in compact mode */}
        <div className={cn(
          'flex items-center gap-1 sm:gap-2',
          isCompact ? 'flex-shrink-0' : ''
        )}>
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={disabled || isProcessing}
            className={cn(
              'flex items-center gap-1 sm:gap-1.5 text-xs sm:text-sm',
              'border border-border rounded-md',
              'text-foreground hover:bg-muted/50',
              'transition-colors',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              isCompact ? 'px-2 py-1' : 'px-3 py-1.5'
            )}
          >
            <Upload className="w-3 h-3 sm:w-3.5 sm:h-3.5" strokeWidth={1.5} />
            <span className={isCompact ? 'hidden sm:inline' : ''}>Files</span>
          </button>

          <button
            onClick={() => folderInputRef.current?.click()}
            disabled={disabled || isProcessing}
            className={cn(
              'flex items-center gap-1 sm:gap-1.5 text-xs sm:text-sm',
              'border border-border rounded-md',
              'text-foreground hover:bg-muted/50',
              'transition-colors',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              isCompact ? 'px-2 py-1' : 'px-3 py-1.5'
            )}
          >
            <FolderOpen className="w-3 h-3 sm:w-3.5 sm:h-3.5" strokeWidth={1.5} />
            <span className={isCompact ? 'hidden sm:inline' : ''}>Folder</span>
          </button>

          <button
            onClick={handlePaste}
            disabled={disabled || isProcessing}
            className={cn(
              'flex items-center gap-1 sm:gap-1.5 text-xs sm:text-sm',
              'border border-border rounded-md',
              'text-foreground hover:bg-muted/50',
              'transition-colors',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              isCompact ? 'px-2 py-1' : 'px-3 py-1.5'
            )}
          >
            <ClipboardPaste className="w-3 h-3 sm:w-3.5 sm:h-3.5" strokeWidth={1.5} />
            <span className={isCompact ? 'hidden sm:inline' : ''}>Paste</span>
          </button>
        </div>
      </div>

      {/* Drag overlay */}
      {isDragging && !disabled && !isProcessing && (
        <div className="absolute inset-0 flex items-center justify-center bg-primary/10 rounded-lg pointer-events-none">
          <div className="text-primary font-medium text-sm">Drop to add sources</div>
        </div>
      )}
    </div>
  )
}
